"use client";

import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "use-intl";

import {
  resolveWebPushControllerStatus,
  type WebPushControllerStatus,
} from "@/hooks/resolveWebPushControllerStatus";
import { useCurrentWebPushCapability } from "@/hooks/use-current-web-push-capability";
import { useDistributionAvailability } from "@/hooks/use-distribution-availability";
import {
  trackNotificationChannelEnabled,
  trackNotificationChannelSetupFailed,
  trackNotificationChannelSetupStarted,
} from "@/lib/analytics";
import { ensureGuestSession } from "@/lib/ensure-guest-session";
import { isWebPushTransportEnabled } from "@/lib/isWebPushTransportEnabled";
import { resolveSuccessfulWebPushBinding } from "@/lib/resolveSuccessfulWebPushBinding";
import { resolveWebPushEnableResult } from "@/lib/resolveWebPushEnableResult";
import { viewerContextQueryOptions } from "@/lib/viewer-context";
import {
  applicationServerKey,
  enableWebPushSubscription,
  hashBrowserPushEndpoint,
  serializePushSubscription,
  type WebPushCapability,
  type WebPushTransport,
} from "@/lib/web-push-browser";
import { loadWebPushRecoveryClient } from "@/lib/webPushRecoveryClient";
import { webPushViewerKey } from "@/lib/webPushViewerKey";
import { client } from "@/utils/orpc.client";
import { normalizeViewerLocale } from "@garimpa/lib/viewerPreferences";

interface PreparedBrowser {
  endpointHash: string | null;
  registration: ServiceWorkerRegistration;
  subscription: PushSubscription | null;
  transport: WebPushTransport;
}

interface UseWebPushControllerInput {
  analyticsSurface: string;
}

interface ConfirmedBinding {
  endpointHash: string;
  viewerKey: string;
}

type SupportedWebPushCapability = Extract<WebPushCapability, { kind: "supported" }>;
type BrowserPreparation =
  | { stage: "wait"; transport: WebPushTransport | null }
  | {
      stage: "static";
      status: "install_required" | "unavailable" | "unsupported";
      transport: WebPushTransport | null;
    }
  | { browser: SupportedWebPushCapability; stage: "ready"; transport: WebPushTransport };

const ENABLE_STATUSES = new Set<WebPushControllerStatus>([
  "inactive",
  "unbound",
  "restore_required",
  "error",
]);
function browserPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

function optionalValue<T>(value: T | null): T | undefined {
  return value === null ? undefined : value;
}

function normalizeControllerError(cause: unknown, fallback: string) {
  return cause instanceof Error ? cause : new Error(fallback);
}

function isConfirmedBinding(
  binding: ConfirmedBinding | null,
  viewerKey: string,
  endpointHash: string | null,
) {
  return [binding?.viewerKey === viewerKey, binding?.endpointHash === endpointHash].every(Boolean);
}

async function subscriptionEndpointHash(subscription: PushSubscription | null) {
  if (!subscription) return null;
  return hashBrowserPushEndpoint(subscription.endpoint);
}

function resolveControllerPresentation(input: {
  capability: WebPushControllerStatus;
  configPublicKey: string | null | undefined;
  configTransports: Parameters<typeof isWebPushTransportEnabled>[0];
  error: Error | null;
  permission: NotificationPermission | null;
  prepared: PreparedBrowser | null;
  resolvedViewerKey: string | null;
  serverBound: boolean | null;
  serverSubscriptionStatus: "active" | "invalid" | "disabled" | "detached" | null;
  viewerKey: string;
}) {
  const viewerStateResolved = input.resolvedViewerKey === input.viewerKey;
  const status = resolveWebPushControllerStatus({
    capability: viewerStateResolved ? input.capability : "preparing",
    error: input.error,
    hasSubscription: Boolean(input.prepared?.subscription),
    permission: input.permission,
    serverBound: viewerStateResolved ? input.serverBound : null,
    serverSubscriptionStatus: input.serverSubscriptionStatus,
  });
  const canEnable = [
    Boolean(input.prepared),
    Boolean(input.configPublicKey),
    input.prepared
      ? isWebPushTransportEnabled(input.configTransports, input.prepared.transport)
      : false,
    ENABLE_STATUSES.has(status),
  ].every(Boolean);

  return { canEnable, status };
}

function resolveEnableInput(
  canEnable: boolean,
  prepared: PreparedBrowser | null,
  publicKey: string | null | undefined,
) {
  if (![canEnable, Boolean(prepared), Boolean(publicKey)].every(Boolean)) return null;
  return { prepared: prepared as PreparedBrowser, publicKey: publicKey as string };
}

async function reusablePushSubscription(
  prepared: PreparedBrowser,
  status: "active" | "invalid" | "disabled" | "detached" | null,
) {
  const current = await prepared.registration.pushManager.getSubscription();
  const reusable =
    current &&
    prepared.endpointHash === (await subscriptionEndpointHash(current)) &&
    (status === "active" || status === "detached");
  if (current && !reusable && !(await current.unsubscribe()))
    throw new Error("Could not remove the old Push subscription");
  return reusable ? current : null;
}

async function disableOwnedBrowser(
  queryClient: QueryClient,
  prepared: PreparedBrowser,
  subscriptionId: string | null,
) {
  const recovery = await loadWebPushRecoveryClient();
  await recovery.setLocalStop("disabled");
  const local = await recovery.readState();
  const endpointHash =
    prepared.endpointHash ?? (await subscriptionEndpointHash(prepared.subscription));
  const viewer = await queryClient.fetchQuery(viewerContextQueryOptions());
  if (!viewer.isAuthenticated) throw new Error("Sign in to disable account notifications");
  const stableId = subscriptionId ?? local.confirmed?.subscriptionId;
  if (!stableId && !endpointHash) throw new Error("Browser Push state is unavailable");
  const result = await client.notifications.unsubscribeCurrentWebPush(
    stableId ? { subscriptionId: stableId } : { endpointHash: endpointHash! },
  );
  if (!result.disabled) throw new Error("Browser Push disable is incomplete");
  await (await prepared.registration.pushManager.getSubscription())?.unsubscribe();
}

function viewerTypeFromKey(identity: "visitor" | "guest" | "member" | undefined) {
  return identity ?? "visitor";
}

function transportFromCapability(browser: WebPushCapability | null) {
  return browser ? browser.transport : null;
}

function isBrowserTransportConfigured(
  browser: Exclude<WebPushCapability, { kind: "unsupported" }>,
  config: {
    available?: boolean;
    publicKey?: string | null;
    transports?: { apns: boolean; fcm: boolean };
  } | null,
) {
  return [
    config?.available,
    Boolean(config?.publicKey),
    isWebPushTransportEnabled(config?.transports, browser.transport),
  ].every(Boolean);
}

function resolveBrowserPreparation(input: {
  browser: WebPushCapability | null;
  config: {
    available?: boolean;
    publicKey?: string | null;
    transports?: { apns: boolean; fcm: boolean };
  } | null;
  configLoading: boolean;
  viewerLoading: boolean;
}): BrowserPreparation {
  const transport = transportFromCapability(input.browser);
  if (!input.browser) return { stage: "wait", transport };
  if (input.browser.kind === "unsupported") {
    return { stage: "static", status: "unsupported", transport };
  }
  if (input.configLoading) return { stage: "wait", transport };

  const configAvailable = isBrowserTransportConfigured(input.browser, input.config);
  if (!configAvailable) return { stage: "static", status: "unavailable", transport };
  if (input.browser.kind === "install_required") {
    return { stage: "static", status: "install_required", transport };
  }
  if (input.viewerLoading) return { stage: "wait", transport };
  return { browser: input.browser, stage: "ready", transport: input.browser.transport };
}

async function fetchWebPushServerState(queryClient: QueryClient, endpointHash?: string) {
  const viewer = await queryClient.fetchQuery(viewerContextQueryOptions());
  if (!viewer.isAuthenticated) {
    const recovery = await loadWebPushRecoveryClient();
    const local = await recovery.readState();
    const confirmed = local.confirmed;
    const currentBrowserSubscribed = Boolean(
      endpointHash &&
      !local.stop &&
      confirmed?.status === "active" &&
      (await hashBrowserPushEndpoint(confirmed.material.endpoint)) === endpointHash,
    );
    return {
      activeBrowserCount: 0,
      currentBrowserSubscribed,
      currentSubscriptionId: null,
      currentSubscriptionStatus: null,
    };
  }
  return client.notifications.getWebPushState({ endpointHash });
}

export function useWebPushController({ analyticsSurface }: UseWebPushControllerInput) {
  const activeLocale = normalizeViewerLocale(useLocale());
  const currentBrowserCapability = useCurrentWebPushCapability();
  const queryClient = useQueryClient();
  const viewerQuery = useQuery(viewerContextQueryOptions());
  const viewerKey = webPushViewerKey(viewerQuery.data);
  const viewerKeyRef = useRef(viewerKey);
  viewerKeyRef.current = viewerKey;
  const distributionQuery = useDistributionAvailability();
  const configQuery = {
    data: distributionQuery.data?.webPush,
    isLoading: distributionQuery.isLoading,
  };
  const [prepared, setPrepared] = useState<PreparedBrowser | null>(null);
  const [capability, setCapability] = useState<WebPushControllerStatus>("preparing");
  const [transport, setTransport] = useState<WebPushTransport | null>(null);
  const [serverBound, setServerBound] = useState<boolean | null>(null);
  const [serverSubscriptionStatus, setServerSubscriptionStatus] = useState<
    "active" | "invalid" | "disabled" | "detached" | null
  >(null);
  const subscriptionIdRef = useRef<string | null>(null);
  const [activeBrowserCount, setActiveBrowserCount] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [resolvedViewerKey, setResolvedViewerKey] = useState<string | null>(null);
  const confirmedBindingRef = useRef<ConfirmedBinding | null>(null);
  const refreshServerState = useCallback(
    async (endpointHash?: string, expectedViewerKey = viewerKeyRef.current) => {
      const state = await fetchWebPushServerState(queryClient, endpointHash);
      if (viewerKeyRef.current !== expectedViewerKey) return false;
      setServerBound(state.currentBrowserSubscribed);
      subscriptionIdRef.current = state.currentSubscriptionId;
      setServerSubscriptionStatus(state.currentSubscriptionStatus);
      setActiveBrowserCount(state.activeBrowserCount);
      setResolvedViewerKey(expectedViewerKey);
      return true;
    },
    [queryClient],
  );

  const refreshActiveBrowserCount = useCallback(
    async (expectedViewerKey = viewerKeyRef.current) => {
      const state = await fetchWebPushServerState(queryClient);
      if (viewerKeyRef.current !== expectedViewerKey) return false;
      setActiveBrowserCount(state.activeBrowserCount);
      return true;
    },
    [queryClient],
  );

  useEffect(() => {
    const preparation = resolveBrowserPreparation({
      browser: currentBrowserCapability,
      config: configQuery.data ?? null,
      configLoading: configQuery.isLoading,
      viewerLoading: viewerQuery.isLoading,
    });
    setTransport(preparation.transport);
    if (preparation.stage === "wait") return;
    if (preparation.stage === "static") {
      confirmedBindingRef.current = null;
      setPrepared(null);
      setServerBound(null);
      setActiveBrowserCount(0);
      setResolvedViewerKey(viewerKey);
      setCapability(preparation.status);
      void refreshActiveBrowserCount(viewerKey).catch(() => undefined);
      return;
    }

    const browser = preparation.browser;
    let cancelled = false;
    const confirmedBinding = confirmedBindingRef.current;
    const hasConfirmedCurrentBinding = confirmedBinding?.viewerKey === viewerKey;
    if (!hasConfirmedCurrentBinding) {
      setPrepared(null);
      setServerBound(null);
      setActiveBrowserCount(0);
      setResolvedViewerKey(null);
      setCapability("preparing");
    }
    setError(null);
    void navigator.serviceWorker
      .register("/push-sw.js", { scope: "/", updateViaCache: "none" })
      .then(() => navigator.serviceWorker.ready)
      .then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        const endpointHash = await subscriptionEndpointHash(subscription);
        if (cancelled) return;
        setPrepared({ registration, subscription, endpointHash, transport: browser.transport });
        const currentConfirmedBinding = confirmedBindingRef.current;
        const confirmedSubscription = isConfirmedBinding(
          currentConfirmedBinding,
          viewerKey,
          endpointHash,
        );
        if (confirmedSubscription) {
          setServerBound(true);
          setResolvedViewerKey(viewerKey);
          setCapability("inactive");
        }

        try {
          const refreshed = await refreshServerState(optionalValue(endpointHash), viewerKey);
          if ([!cancelled, refreshed].every(Boolean)) setCapability("inactive");
        } catch (cause: unknown) {
          if (cancelled) return;
          const latestConfirmedBinding = confirmedBindingRef.current;
          if (isConfirmedBinding(latestConfirmedBinding, viewerKey, endpointHash)) {
            setServerBound(true);
            setResolvedViewerKey(viewerKey);
            setError(null);
            setCapability("inactive");
            return;
          }
          setError(normalizeControllerError(cause, "Could not refresh Browser Push"));
          setResolvedViewerKey(viewerKey);
          setCapability("error");
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const latestConfirmedBinding = confirmedBindingRef.current;
        if (latestConfirmedBinding?.viewerKey === viewerKey) {
          setServerBound(true);
          setResolvedViewerKey(viewerKey);
          setError(null);
          setCapability("inactive");
          return;
        }
        setError(normalizeControllerError(cause, "Could not prepare Browser Push"));
        setResolvedViewerKey(viewerKey);
        setCapability("error");
      });

    return () => {
      cancelled = true;
    };
  }, [
    configQuery.data,
    configQuery.isLoading,
    currentBrowserCapability,
    refreshActiveBrowserCount,
    refreshServerState,
    refreshVersion,
    viewerQuery.isLoading,
    viewerKey,
  ]);

  const permission = browserPermission();
  const { canEnable, status } = resolveControllerPresentation({
    capability,
    configPublicKey: configQuery.data?.publicKey,
    configTransports: configQuery.data?.transports,
    error,
    permission,
    prepared,
    resolvedViewerKey,
    serverBound,
    serverSubscriptionStatus,
    viewerKey,
  });
  const viewerType = viewerTypeFromKey(viewerQuery.data?.identity);

  const enable = useCallback(async () => {
    const enableInput = resolveEnableInput(canEnable, prepared, configQuery.data?.publicKey);
    if (!enableInput) return false;
    const { prepared: enablePrepared, publicKey } = enableInput;

    const permissionBefore = browserPermission();
    const startedViewerKey = viewerKeyRef.current;
    setIsPending(true);
    setError(null);
    trackNotificationChannelSetupStarted("web_push", analyticsSurface);

    try {
      // Start a first subscription from the click before storage or script loading.
      const browserSubscription = enablePrepared.subscription
        ? reusablePushSubscription(enablePrepared, serverSubscriptionStatus)
        : enablePrepared.registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(publicKey),
          });
      const recoveryState = loadWebPushRecoveryClient().then(async (recovery) => ({
        recovery,
        local: await recovery.readState(),
      }));
      const [current, { recovery, local }] = await Promise.all([
        browserSubscription,
        recoveryState,
      ]);
      const enabled = await enableWebPushSubscription({
        subscription: current,
        subscribe: () =>
          enablePrepared.registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(publicKey),
          }),
        ensureSession: () => ensureGuestSession(queryClient),
        bind: (candidate) =>
          client.notifications.subscribeWebPush(serializePushSubscription(candidate, activeLocale)),
      });
      const permissionAfter = browserPermission();

      if (!enabled.ok) {
        setPrepared((current) =>
          current ? { ...current, subscription: enabled.subscription } : current,
        );
        setServerBound(false);
        setError(enabled.error);
        trackNotificationChannelSetupFailed(
          "web_push",
          analyticsSurface,
          resolveWebPushEnableResult({
            errorName: enabled.error.name,
            outcome: enabled.stage,
            permissionAfter,
            permissionBefore,
          }),
        );
        return false;
      }

      const saved = await recovery.saveConfirmed({
        result: enabled.result,
        material: (() => {
          const { locale: _locale, ...material } = serializePushSubscription(
            enabled.subscription,
            activeLocale,
          );
          return material;
        })(),
        vapidPublicKey: publicKey,
        generation: local.generation,
      });
      if (!saved) throw new Error("Browser Push changed during activation; refresh its state");
      subscriptionIdRef.current = enabled.result.subscriptionId;
      setServerSubscriptionStatus("active");
      setPrepared({
        ...enablePrepared,
        subscription: enabled.subscription,
        endpointHash: enabled.result.endpointHash,
      });
      const successfulBinding = resolveSuccessfulWebPushBinding(enabled.session, {
        currentViewerKey: viewerKeyRef.current,
        startedViewerKey,
      });
      if (successfulBinding) {
        trackNotificationChannelEnabled("web_push", analyticsSurface);
        confirmedBindingRef.current = {
          endpointHash: enabled.result.endpointHash,
          viewerKey: successfulBinding.resolvedViewerKey,
        };
        setServerBound(successfulBinding.serverBound);
        setResolvedViewerKey(successfulBinding.resolvedViewerKey);
        setCapability("inactive");
        setError(null);
      }
      const boundViewerKey = webPushViewerKey(enabled.session);
      void refreshServerState(enabled.result.endpointHash, boundViewerKey).catch(() => {
        trackNotificationChannelSetupFailed("web_push", analyticsSurface, "state_refresh_error");
      });

      return successfulBinding !== null;
    } catch (cause) {
      setError(normalizeControllerError(cause, "Could not restore Browser Push"));
      return false;
    } finally {
      setIsPending(false);
    }
  }, [
    serverSubscriptionStatus,
    analyticsSurface,
    activeLocale,
    canEnable,
    configQuery.data?.publicKey,
    prepared,
    queryClient,
    refreshServerState,
  ]);

  const refresh = useCallback(() => {
    trackNotificationChannelSetupStarted("web_push", analyticsSurface);
    setError(null);
    setCapability("preparing");
    setRefreshVersion((current) => current + 1);
  }, [analyticsSurface]);

  const disableCurrent = useCallback(async () => {
    if (!prepared) return;
    setIsPending(true);
    setError(null);
    try {
      await disableOwnedBrowser(queryClient, prepared, subscriptionIdRef.current);
      confirmedBindingRef.current = null;
      setPrepared({ ...prepared, subscription: null, endpointHash: null });
      setServerBound(false);
      await refreshServerState();
    } catch (cause) {
      setError(normalizeControllerError(cause, "Could not disable Browser Push"));
    } finally {
      setIsPending(false);
    }
  }, [prepared, queryClient, refreshServerState]);

  const disableAll = useCallback(async () => {
    setIsPending(true);
    setError(null);
    try {
      const recovery = await loadWebPushRecoveryClient();
      await recovery.setLocalStop("disabled");
      const viewer = await queryClient.fetchQuery(viewerContextQueryOptions());
      if (!viewer.isAuthenticated) throw new Error("Sign in to disable account notifications");
      await client.notifications.unsubscribeAllWebPush();
      await prepared?.subscription?.unsubscribe();
      confirmedBindingRef.current = null;
      setPrepared((current) =>
        current ? { ...current, subscription: null, endpointHash: null } : current,
      );
      setServerBound(false);
      setActiveBrowserCount(0);
    } catch (cause) {
      setError(normalizeControllerError(cause, "Could not disable Browser Push"));
    } finally {
      setIsPending(false);
    }
  }, [prepared, queryClient]);

  return {
    activeBrowserCount,
    canEnable,
    canDisableCurrent:
      serverSubscriptionStatus === "active" || serverSubscriptionStatus === "invalid",
    disableAll,
    disableCurrent,
    enable,
    error,
    isPending,
    permission,
    refresh,
    status,
    transport,
    viewerType,
  };
}
